import { useState, useRef } from 'react'
import { User } from '../types'
import UserAutocomplete from './UserAutocomplete'
import { DetailsModal } from './DetailsModal'
import './UserManagement.css'

interface UserManagementProps {
  users: User[]
  loading?: boolean
  onAddUser: (user: Omit<User, 'id'>) => Promise<void>
  onUpdateUser: (userId: string, user: Omit<User, 'id'>) => Promise<void>
  onDeleteUser: (userId: string) => void
  onRefresh?: () => void
}

// Uživatelé se specialní rolí + zaměstnanci školy bez role (z personalistiky)
function filterUsersForManagement(users: User[]): User[] {
  return users.filter((user) => {
    const role = user.role
    if (role && role !== 'requester') {
      return true
    }
    const email = (user.email || '').trim().toLowerCase()
    if (user.user_type === 'employee') {
      return true
    }
    if (email.endsWith('@iss-cheb.cz') && !email.endsWith('@student.iss-cheb.cz')) {
      return true
    }
    return false
  })
}

function UserManagement({ users, loading, onAddUser, onUpdateUser, onDeleteUser, onRefresh: _onRefresh }: UserManagementProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)
  const [formData, setFormData] = useState<{
    name: string
    email: string
    role: 'admin' | 'executor' | 'director' | 'financist' | 'referent' | 'referent_eko' | 'vedouci' | 'vedouci_executor' | 'property_referent' | 'spravce_rozvrhu' | 'spravce_jidelny' | 'personalista' | 'zocmos' | 'zastupce_reditele'
    adminType: 'spravce_it' | 'spravce_budov' | undefined
  }>({
    name: '',
    email: '',
    role: 'executor',
    adminType: 'spravce_budov',
  })

  const handleUserSelect = (user: User | null) => {
    setSelectedUser(user)
    if (user) {
      // При выборе пользователя заполняем имя и email автоматически
      const userRole = user.role as 'admin' | 'executor' | 'director' | 'financist' | 'referent' | 'referent_eko' | 'vedouci' | 'vedouci_executor' | 'property_referent' | 'spravce_rozvrhu' | 'spravce_jidelny' | 'personalista' | 'zocmos' | 'zastupce_reditele' | null
      setFormData({
        name: user.name,
        email: user.email,
        role: userRole || 'executor',
        adminType: user.admin_type || 'spravce_budov',
      })
    } else {
      // Если очистили выбор, очищаем только имя, email оставляем для ручного ввода
      setFormData({
        ...formData,
        name: '',
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.email) {
      alert('Prosím vyplňte všechna pole')
      return
    }

    const userData: Omit<User, 'id'> = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      admin_type: formData.role === 'admin' ? formData.adminType : undefined,
    }

    console.log('Submitting user data:', userData, 'Role:', formData.role)

    try {
      if (editingUser) {
        // Редактирование существующего пользователя
        console.log('Updating user:', editingUser.id, 'with role:', userData.role, 'Role type:', typeof userData.role)
        await onUpdateUser(editingUser.id, userData)
        setEditingUser(null)
      } else {
        // Создание нового пользователя
        await onAddUser(userData)
      }
      
      setFormData({ name: '', email: '', role: 'executor', adminType: 'spravce_budov' })
      setSelectedUser(null)
      setShowAddForm(false)
    } catch (error) {
      // Ошибка уже обработана в handleUpdateUser/handleAddUser
      // Не закрываем форму при ошибке
      console.error('Error in handleSubmit:', error)
    }
  }

  const handleRowClick = (user: User) => {
    setSelectedUser(user)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedUser(null)
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setShowAddForm(true)
    const userRole = user.role as 'admin' | 'executor' | 'director' | 'financist' | 'referent' | 'referent_eko' | 'vedouci' | 'vedouci_executor' | 'property_referent' | 'spravce_rozvrhu' | 'spravce_jidelny' | 'personalista' | 'zocmos' | 'zastupce_reditele' | null
    setFormData({
      name: user.name,
      email: user.email,
      role: userRole || 'executor',
      adminType: user.admin_type || 'spravce_budov',
    })
    setSelectedUser(null)
    
    // Прокручиваем к форме после небольшой задержки, чтобы форма успела отрендериться
    setTimeout(() => {
      const formElement = document.getElementById('user-edit-form')
      if (formElement) {
        // Получаем позицию формы относительно документа
        const formTop = formElement.getBoundingClientRect().top + window.pageYOffset
        // Прокручиваем к форме с отступом сверху (120px для навигации и отступов)
        const scrollPosition = formTop - 120
        
        window.scrollTo({
          top: Math.max(0, scrollPosition), // Убеждаемся, что не прокручиваем выше начала страницы
          behavior: 'smooth'
        })
      } else if (formRef.current) {
        // Fallback на ref, если id не сработал
        const formTop = formRef.current.getBoundingClientRect().top + window.pageYOffset
        window.scrollTo({
          top: Math.max(0, formTop - 120),
          behavior: 'smooth'
        })
      }
    }, 200)
  }

  const handleCancel = () => {
    setShowAddForm(false)
    setEditingUser(null)
    setSelectedUser(null)
    setFormData({ name: '', email: '', role: 'executor', adminType: 'spravce_budov' })
  }

  // Seznam pro správu: role + zaměstnanci bez role / žadatel
  const usersWithRoles = filterUsersForManagement(users)
  
  const filteredUsers = roleFilter === 'all' 
    ? usersWithRoles 
    : roleFilter === 'bez_role'
      ? usersWithRoles.filter((user) => !user.role || user.role === 'requester')
      : usersWithRoles.filter(user => {
        if (roleFilter === 'admin') {
          return user.role === 'admin'
        }
        return user.role === roleFilter
      })

  const getRoleLabel = (role: string | null, adminType?: string) => {
    if (!role || role === 'requester') {
      return role === 'requester' ? 'Žadatel (bez speciální role)' : 'Bez role'
    }
    if (role === 'admin') {
      return adminType === 'spravce_it' ? 'Správce IT' : 'Správce Budov'
    }
    switch (role) {
      case 'executor': return 'Řešitel'
      case 'vedouci_executor': return 'Řešitel-Vedoucí'
      case 'director': return 'Ředitel'
      case 'financist': return 'Hlavní ekonom'
      case 'referent': return 'Obchodní referent'
      case 'referent_eko': return 'Referent EKO'
      case 'vedouci': return 'Vedoucí'
      case 'property_referent': return 'Referent správy majetku'
      case 'spravce_rozvrhu': return 'Správce rozvrhů'
      case 'spravce_jidelny': return 'Správce jídelny'
      case 'personalista': return 'Personalista'
      case 'zocmos': return 'ZO ČMOS (Odbory)'
      case 'zastupce_reditele': return 'Zástupce ředitele'
      default: return role
    }
  }

  // Подсчет пользователей по ролям
  const roleCounts = usersWithRoles.reduce((acc, user) => {
    const role = !user.role || user.role === 'requester' ? 'bez_role' : user.role
    acc[role] = (acc[role] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  return (
    <div className="user-management">
      <div className="management-header">
        <h2>Správa uživatelů</h2>
        <button 
          className="add-button"
          onClick={() => {
            if (showAddForm) {
              handleCancel()
            } else {
              setShowAddForm(true)
              setEditingUser(null)
            }
          }}
        >
          {showAddForm ? '✕ Zrušit' : '+ Přidat uživatele'}
        </button>
      </div>

      {/* Фильтр по ролям */}
      <div className="role-filter" style={{ 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#f5f5f5', 
        borderRadius: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        alignItems: 'center'
      }}>
        <span style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Filtr:</span>
        <button
          onClick={() => setRoleFilter('all')}
          style={{
            padding: '0.5rem 1rem',
            border: roleFilter === 'all' ? '2px solid #2c5aa0' : '1px solid #ddd',
            backgroundColor: roleFilter === 'all' ? '#2c5aa0' : 'white',
            color: roleFilter === 'all' ? 'white' : '#333',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: roleFilter === 'all' ? 'bold' : 'normal'
          }}
        >
          Všichni ({usersWithRoles.length})
        </button>
        {(roleCounts['bez_role'] || 0) > 0 && (
          <button
            onClick={() => setRoleFilter('bez_role')}
            style={{
              padding: '0.5rem 1rem',
              border: roleFilter === 'bez_role' ? '2px solid #2c5aa0' : '1px solid #ddd',
              backgroundColor: roleFilter === 'bez_role' ? '#2c5aa0' : 'white',
              color: roleFilter === 'bez_role' ? 'white' : '#333',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: roleFilter === 'bez_role' ? 'bold' : 'normal'
            }}
          >
            Bez role ({roleCounts['bez_role']})
          </button>
        )}
        {['admin', 'executor', 'vedouci_executor', 'director', 'financist', 'referent', 'referent_eko', 'vedouci', 'property_referent', 'spravce_rozvrhu', 'spravce_jidelny', 'personalista', 'zocmos', 'zastupce_reditele'].map(role => (
          roleCounts[role] > 0 && (
            <button
              key={role}
              onClick={() => setRoleFilter(role)}
              style={{
                padding: '0.5rem 1rem',
                border: roleFilter === role ? '2px solid #2c5aa0' : '1px solid #ddd',
                backgroundColor: roleFilter === role ? '#2c5aa0' : 'white',
                color: roleFilter === role ? 'white' : '#333',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: roleFilter === role ? 'bold' : 'normal'
              }}
            >
              {getRoleLabel(role, undefined)} ({roleCounts[role]})
            </button>
          )
        ))}
      </div>

      {showAddForm && (
        <div id="user-edit-form" className="add-user-form" ref={formRef}>
          <h3 style={{ marginBottom: '1rem', color: editingUser ? '#2c5aa0' : '#333' }}>
            {editingUser ? '✏️ Upravit uživatele' : '+ Přidat nového uživatele'}
          </h3>
          <form onSubmit={handleSubmit}>
            {!editingUser && (
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <UserAutocomplete
                  value={selectedUser}
                  onChange={handleUserSelect}
                  placeholder="Začněte psát email nebo jméno..."
                  required={false}
                  filterAvailableUsers={false}
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                  Při výběru se jméno vyplní automaticky
                </small>
              </div>
            )}
            {editingUser && (
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  disabled
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                  }}
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                  Email nelze změnit
                </small>
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="name">Jméno *</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jméno a příjmení"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="role">Role *</label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value as 'admin' | 'executor' | 'director' | 'financist' | 'referent' | 'referent_eko' | 'vedouci' | 'vedouci_executor' | 'property_referent' | 'spravce_rozvrhu' | 'spravce_jidelny' | 'personalista' | 'zocmos' | 'zastupce_reditele'
                  setFormData({ 
                    ...formData, 
                    role: newRole,
                    adminType: newRole === 'admin' ? formData.adminType : undefined
                  })
                }}
                required
              >
                <option value="executor">Řešitel</option>
                <option value="vedouci_executor">Řešitel-Vedoucí</option>
                <option value="admin">Správce</option>
                <option value="director">Ředitel</option>
                <option value="financist">Hlavní ekonom</option>
                <option value="referent">Obchodní referent</option>
                <option value="referent_eko">Referent EKO</option>
                <option value="vedouci">Vedoucí</option>
                <option value="property_referent">Referent správy majetku</option>
                <option value="spravce_rozvrhu">Správce rozvrhů</option>
                <option value="spravce_jidelny">Správce jídelny</option>
                <option value="personalista">Personalista</option>
                <option value="zocmos">ZO ČMOS (Odbory)</option>
                <option value="zastupce_reditele">Zástupce ředitele</option>
              </select>
            </div>
            {formData.role === 'admin' && (
              <div className="form-group">
                <label htmlFor="adminType">Typ správce *</label>
                <select
                  id="adminType"
                  value={formData.adminType || 'spravce_budov'}
                  onChange={(e) => setFormData({ ...formData, adminType: e.target.value as 'spravce_it' | 'spravce_budov' })}
                  required
                >
                  <option value="spravce_it">Správce IT</option>
                  <option value="spravce_budov">Správce Budov</option>
                </select>
              </div>
            )}
            <div className="form-actions">
              <button type="submit" className="submit-button">
                {editingUser ? 'Uložit změny' : 'Přidat'}
              </button>
              <button type="button" onClick={handleCancel} className="cancel-button">
                Zrušit
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading">Načítání...</div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Jméno</th>
                <th>Email</th>
                <th>Role</th>
                <th>Akce</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                if (filteredUsers.length === 0) {
                  return (
                  <tr>
                    <td colSpan={4} className="empty-message">
                      {usersWithRoles.length === 0
                        ? 'Žádní uživatelé ke správě'
                        : 'Žádní uživatelé v tomto filtru'}
                    </td>
                  </tr>
                  )
                }
                return filteredUsers.map(user => {

                  const getRoleClass = (role: string | null, adminType?: string) => {
                    if (role === 'admin') {
                      return adminType === 'spravce_it' ? 'role-admin-it' : 'role-admin-buildings'
                    }
                    switch (role) {
                      case 'executor':
                        return 'role-executor'
                      case 'vedouci_executor':
                        return 'role-vedouci_executor'
                      case 'director':
                        return 'role-director'
                      case 'financist':
                        return 'role-financist'
                      case 'referent':
                        return 'role-referent'
                      case 'referent_eko':
                        return 'role-referent'
                      case 'vedouci':
                        return 'role-vedouci'
                      case 'property_referent':
                        return 'role-property-referent'
                      case 'personalista':
                        return 'role-personalista'
                      case 'zocmos':
                        return 'role-zocmos'
                      case 'zastupce_reditele':
                        return 'role-zastupce-reditele'
                      default:
                        return ''
                    }
                  }
                  
                  return (
                    <tr 
                      key={user.id} 
                      className={`clickable-row ${getRoleClass(user.role, user.admin_type)}`}
                      onClick={() => handleRowClick(user)}
                    >
                      <td><strong>{user.name}</strong></td>
                      <td>{user.email}</td>
                      <td>
                        {getRoleLabel(user.role, user.admin_type)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="edit-button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEdit(user)
                            }}
                            title="Upravit uživatele"
                            style={{
                              background: '#2c5aa0',
                              color: 'white',
                              border: 'none',
                              padding: '0.4rem 0.6rem',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#1e3f73'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#2c5aa0'
                            }}
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-button"
                            onClick={(e) => {
                              e.stopPropagation()
                              onDeleteUser(user.id)
                            }}
                            title="Odstranit uživatele"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                  })
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* Модальное окно с деталями */}
      {selectedUser && (
        <DetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={`Uživatel: ${selectedUser.name}`}
          actions={
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="edit-button"
                onClick={() => {
                  handleCloseModal()
                  handleEdit(selectedUser)
                }}
                style={{
                  background: '#2c5aa0',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✏️ Upravit
              </button>
              <button
                className="delete-button"
                onClick={() => {
                  if (confirm(`Opravdu chcete smazat uživatele ${selectedUser.name}?`)) {
                    onDeleteUser(selectedUser.id)
                    handleCloseModal()
                  }
                }}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🗑️ Smazat
              </button>
            </div>
          }
        >
          <div className="details-section">
            <div className="details-grid">
              <div className="details-item">
                <div className="details-label">Jméno</div>
                <div className="details-value"><strong>{selectedUser.name}</strong></div>
              </div>
              <div className="details-item">
                <div className="details-label">Email</div>
                <div className="details-value">
                  <a href={`mailto:${selectedUser.email}`} className="email-link">
                    {selectedUser.email}
                  </a>
                </div>
              </div>
              <div className="details-item">
                <div className="details-label">Role</div>
                <div className="details-value">
                  {getRoleLabel(selectedUser.role, selectedUser.admin_type)}
                </div>
              </div>
            </div>
          </div>
        </DetailsModal>
      )}
    </div>
  )
}

export default UserManagement

